"use client";

import type { ReactNode } from "react";
import { Box, HStack, Stack, Text, VStack } from "@chakra-ui/react";
import { PROFILE_SHAPING_STUDIO_GUIDANCE } from "@/features/profile-shaping/config/actions";
import GenericInteractiveGuidance from "@/shared/ui/display/GenericInteractiveGuidance";
import StudioGroupingControl from "./StudioGroupingControl";
import StudioModePanel from "./StudioModePanel";
import StudioPrototypeSwitcher from "./StudioPrototypeSwitcher";
import StudioQuickActionButton from "./StudioQuickActionButton";
import StudioSituationLoadManager from "./StudioSituationLoadManager";
import { PROTOTYPE_FRAME_TONES } from "./studioChrome.constants";
import type { StudioScreenState } from "./useStudioScreen";

type StudioChromeProps = {
  children: ReactNode;
  screen: StudioScreenState;
  selectionActions?: ReactNode;
};

function formatCount(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

export default function StudioChrome({
  children,
  screen,
  selectionActions,
}: StudioChromeProps) {
  const {
    activePrototypeCategoryForUi,
    activeStudioGroupingForUi,
    handlePrototypeCategoryChange,
    handleStudioGroupingChange,
    isLocked,
    studioQuickActions,
    handleStudioQuickActionClick,
    hasSelectionActionBar,
    selectedSituationsCountForUi,
    selectedThoughtsCountForUi,
    visibleSituationsCountForUi,
    visibleThoughtsCountForUi,
    reviewDescription,
    isPreviewUnavailable,
    isNeedsAnchorMode,
    maintenanceModeHumanReadable,
    previewButtonColorPalette,
    previewButtonIcon,
    previewButtonHumanReadable,
    shouldShowReviewDescription,
    handleTogglePreviewClick,
    handleOpenRerankDialog,
    handleExitMaintenanceMode,
    studioPrototypeItemsWithCounts,
    studioGroupingItems,
    shouldShowManualAddGuidance,
    handleDismissManualAddGuidance,
    shouldShowLoadedSelectionSearchGuidance,
    handleDismissLoadedSelectionSearchGuidance,
    studioSituationLoadManagerItems,
    loadedSituationManagerItemCount,
    availableSituationManagerItemCount,
    hiddenSituationManagerItemCount,
    handleStudioSituationLoadChange,
    handleLoadAllStudioSituations,
  } = screen;

  const isOrderMode = isLocked;
  const activeFrameTone = PROTOTYPE_FRAME_TONES[activePrototypeCategoryForUi];
  const visibleLabel = isOrderMode
    ? "Order selection"
    : [
        formatCount(visibleSituationsCountForUi, "visible situation"),
        formatCount(visibleThoughtsCountForUi, "visible thought"),
        hiddenSituationManagerItemCount > 0
          ? formatCount(hiddenSituationManagerItemCount, "hidden situation")
          : null,
      ]
        .filter(Boolean)
        .join(" / ");
  const mobileVisibleLabel = isOrderMode
    ? "Order selection"
    : [
        formatCount(visibleSituationsCountForUi, "situation"),
        formatCount(visibleThoughtsCountForUi, "thought"),
        hiddenSituationManagerItemCount > 0
          ? `${hiddenSituationManagerItemCount} hidden`
          : null,
      ]
        .filter(Boolean)
        .join(" / ");
  const selectedLabel = [
    formatCount(selectedSituationsCountForUi, "selected situation"),
    formatCount(selectedThoughtsCountForUi, "selected thought"),
  ].join(" / ");
  const selectVisibleAction = studioQuickActions.find(
    (action) => action.key === "select_visible",
  );
  const secondaryQuickActions = studioQuickActions.filter(
    (action) => action.key !== "select_visible",
  );

  return (
    <VStack gap="3" align="stretch">
      <VStack w="full" align="stretch" gap="2">
        <HStack
          align="center"
          justify="space-between"
          gap={{ base: "2", md: "4" }}
          bg="moveon.surface.toolbarBg"
          borderWidth="1"
          borderColor="moveon.surface.border"
          borderRadius="md"
          px={{ base: "2", md: "4" }}
          py={{ base: "2", md: "2.5" }}
        >
          <HStack gap={{ base: "2", md: "3" }} minW="0" flex="1" align="center">
            {selectVisibleAction ? (
              <StudioSituationLoadManager
                action={selectVisibleAction}
                availableCount={availableSituationManagerItemCount}
                items={studioSituationLoadManagerItems}
                loadedCount={loadedSituationManagerItemCount}
                onLoadAll={handleLoadAllStudioSituations}
                onSelectVisible={handleStudioQuickActionClick}
                onSituationLoadChange={handleStudioSituationLoadChange}
              />
            ) : null}

            <Stack gap="0.5" minW={0} flex="1">
              <Text
                fontSize={{ base: "xs", sm: "sm", md: "md" }}
                fontWeight="semibold"
                color="moveon.surface.fg"
                lineHeight="short"
                truncate
              >
                Studio workspace
              </Text>
              <HStack gap="1.5" flexWrap="nowrap" minW="0">
                <Text
                  fontSize="xs"
                  color="moveon.text.muted"
                  lineHeight="short"
                  truncate
                  display={{ base: "block", md: "none" }}
                >
                  {mobileVisibleLabel}
                </Text>
                <Text
                  fontSize="xs"
                  color="moveon.text.muted"
                  lineHeight="short"
                  truncate
                  display={{ base: "none", md: "block" }}
                >
                  {visibleLabel}
                </Text>
                {hasSelectionActionBar ? (
                  <HStack
                    as="span"
                    gap="2"
                    flexShrink="0"
                    display={{ base: "none", md: "flex" }}
                  >
                    <Text
                      aria-hidden="true"
                      fontSize="xs"
                      color="moveon.text.muted"
                      lineHeight="short"
                    >
                      /
                    </Text>
                    <Text
                      fontSize="xs"
                      color="moveon.text.muted"
                      lineHeight="short"
                    >
                      {selectedLabel}
                    </Text>
                  </HStack>
                ) : null}
              </HStack>
            </Stack>
          </HStack>

          <HStack
            gap="1.5"
            align="center"
            justify="flex-end"
            flexShrink="0"
            flexWrap="nowrap"
          >
            {secondaryQuickActions.map((action) => (
              <StudioQuickActionButton
                key={action.key}
                action={action}
                onActionClick={handleStudioQuickActionClick}
              />
            ))}
          </HStack>
        </HStack>
      </VStack>

      <Stack
        direction={{ base: "column", md: "row" }}
        align="flex-start"
        gap={{ base: "2", md: "3" }}
      >
        {hasSelectionActionBar ? selectionActions : null}

        <Box
          borderWidth="1"
          borderColor="moveon.surface.border"
          borderTopWidth="3"
          borderTopColor={activeFrameTone.accent}
          borderRadius="md"
          bg="moveon.surface.bg"
          overflow="hidden"
          flex="1"
          minW="0"
          w="full"
        >
          <VStack align="stretch" gap="0">
            <Box
              px={{ base: "2", md: "4" }}
              py={{ base: "2", md: "4" }}
              borderBottomWidth="1"
              borderBottomColor="moveon.surface.border"
              bg="moveon.surface.toolbarBg"
            >
              <VStack align="stretch" gap={{ base: "2", md: "3" }}>
                <HStack
                  justify="space-between"
                  align="stretch"
                  gap="3"
                  flexWrap="wrap"
                >
                  <Box minW={0} flex="1" display="flex" alignSelf="stretch">
                    <StudioPrototypeSwitcher
                      activePrototypeCategory={activePrototypeCategoryForUi}
                      options={studioPrototypeItemsWithCounts}
                      tone={activeFrameTone}
                      onPrototypeCategoryChange={handlePrototypeCategoryChange}
                    />
                  </Box>

                  <VStack
                    align={{ base: "stretch", md: "flex-end" }}
                    justify="center"
                    gap="2"
                    minH={{ base: "12", md: "14" }}
                    alignSelf="stretch"
                  >
                    <StudioGroupingControl
                      activeStudioGrouping={activeStudioGroupingForUi}
                      options={studioGroupingItems}
                      disabled={isNeedsAnchorMode}
                      tone={activeFrameTone}
                      onStudioGroupingChange={handleStudioGroupingChange}
                    />
                  </VStack>
                </HStack>

                <StudioModePanel
                  tone={activeFrameTone}
                  isOrderMode={isOrderMode}
                  isNeedsAnchorMode={isNeedsAnchorMode}
                  maintenanceModeHumanReadable={maintenanceModeHumanReadable}
                  isPreviewUnavailable={isPreviewUnavailable}
                  previewButtonColorPalette={previewButtonColorPalette}
                  previewButtonIcon={previewButtonIcon}
                  previewButtonHumanReadable={previewButtonHumanReadable}
                  reviewDescription={reviewDescription}
                  shouldShowReviewDescription={shouldShowReviewDescription}
                  onTogglePreviewClick={handleTogglePreviewClick}
                  onOpenRerankDialog={handleOpenRerankDialog}
                  onExitMaintenanceMode={handleExitMaintenanceMode}
                />
              </VStack>
            </Box>

            <Box px={{ base: "1.5", md: "4" }} py={{ base: "2", md: "4" }}>
              {shouldShowManualAddGuidance ? (
                <GenericInteractiveGuidance
                  title={PROFILE_SHAPING_STUDIO_GUIDANCE.manualAdd.title}
                  message={PROFILE_SHAPING_STUDIO_GUIDANCE.manualAdd.message}
                  compactMessage={
                    PROFILE_SHAPING_STUDIO_GUIDANCE.manualAdd.compactMessage
                  }
                  icon="situation"
                  accentColor={activeFrameTone.accent}
                  accentBg={activeFrameTone.accentBg}
                  accentBorderColor={activeFrameTone.accentBorder}
                  dismissLabel="Dismiss profile shaping guidance"
                  onDismiss={handleDismissManualAddGuidance}
                />
              ) : null}
              {shouldShowLoadedSelectionSearchGuidance ? (
                <GenericInteractiveGuidance
                  title={
                    PROFILE_SHAPING_STUDIO_GUIDANCE.loadedSelectionSearch.title
                  }
                  message={
                    PROFILE_SHAPING_STUDIO_GUIDANCE.loadedSelectionSearch
                      .message
                  }
                  compactMessage={
                    PROFILE_SHAPING_STUDIO_GUIDANCE.loadedSelectionSearch
                      .compactMessage
                  }
                  icon="load"
                  accentColor={activeFrameTone.accent}
                  accentBg={activeFrameTone.accentBg}
                  accentBorderColor={activeFrameTone.accentBorder}
                  dismissLabel="Dismiss profile version guidance"
                  onDismiss={handleDismissLoadedSelectionSearchGuidance}
                />
              ) : null}
              {children}
            </Box>
          </VStack>
        </Box>
      </Stack>
    </VStack>
  );
}
